/**
 * LoginPage — `/login`.
 *
 * Minimal email + password form. There's no signup or password-reset
 * flow yet — accounts are pre-seeded for the MVP demo. Once the user
 * succeeds we route them to wherever they were trying to go (the
 * `?next=` param), defaulting to `/`.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";

import { ApiError } from "../../lib/api";
import { env } from "../../lib/env";
import { useAuth } from "../AuthContext";

export function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // Seed from `?sso_error=` so a failed SSO round-trip lands back here with a
  // readable message instead of a blank form.
  const [error, setError] = useState<string | null>(params.get("sso_error"));

  // Single sign-on entry: the user types their workspace slug, and we send
  // the browser to the workspace's SSO initiation endpoint (which 302s to
  // their IdP). A full navigation — not a fetch — so the IdP redirect works.
  const [showSso, setShowSso] = useState(false);
  const [ssoSlug, setSsoSlug] = useState("");

  function startSso(e: React.FormEvent) {
    e.preventDefault();
    const slug = ssoSlug.trim().toLowerCase();
    if (!slug) return;
    const nextParam = encodeURIComponent(next === "/" ? "/home" : next);
    window.location.href =
      `${env.apiBaseUrl}/api/v1/auth/sso/${encodeURIComponent(slug)}/login` +
      `?next=${nextParam}`;
  }

  // If we land here while already authenticated (manual nav, refresh
  // mid-session) bounce straight to the destination. Done in
  // useEffect rather than at render time — calling navigate() during
  // render is illegal and triggers React warnings + occasional
  // "loop where logout-then-immediate-redirect-back" symptoms.
  useEffect(() => {
    if (status === "authenticated") {
      navigate(next, { replace: true });
    }
  }, [status, next, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // status 0 = the API never responded (container down, CORS, offline).
        // We threw a structured ApiError from lib/api with a useful message;
        // surface it directly so the user knows what to check, instead of
        // showing the generic "Login failed".
        if (err.status === 0) {
          setError(err.message);
        } else {
          const detail = (err.body as { detail?: string } | undefined)?.detail;
          setError(detail || "Login failed");
        }
      } else {
        setError(err instanceof Error ? err.message : "Login failed");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen bg-white">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between bg-slate-900 p-12">
        <img src="/meridian-lockup-white.svg" alt="Meridian" className="h-8 w-auto" />
        <div>
          <blockquote className="text-2xl font-light leading-relaxed text-slate-200">
            "The fastest way to understand what's actually happening in your business — and what to do next."
          </blockquote>
          <div className="mt-6 flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-700">
              <Sparkles className="h-4 w-4 text-slate-300" />
            </div>
            <span className="text-sm text-slate-400">Powered by Meridian AI</span>
          </div>
        </div>
        <div className="flex gap-6 text-[11px] text-slate-600">
          <span>© 2026 Meridian</span>
          <a href="mailto:hello@meridian.app" className="hover:text-slate-400 transition-colors">Contact</a>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex w-full lg:w-1/2 flex-col items-center justify-center px-8 py-12">
        {/* Mobile logo */}
        <div className="mb-10 lg:hidden">
          <img src="/meridian-lockup.svg" alt="Meridian" className="h-8 w-auto" />
        </div>

        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-slate-900">Welcome back</h1>
            <p className="mt-1.5 text-sm text-slate-500">
              Sign in to your Meridian workspace.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="text-[12px] font-medium text-slate-700">Email</span>
              <input
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 block w-full rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[14px] text-slate-900 placeholder-slate-400 focus:border-slate-400 focus:bg-white focus:outline-none transition-colors"
                placeholder="you@example.com"
              />
            </label>

            <label className="block">
              <span className="text-[12px] font-medium text-slate-700">Password</span>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 block w-full rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[14px] text-slate-900 placeholder-slate-400 focus:border-slate-400 focus:bg-white focus:outline-none transition-colors"
                placeholder="••••••••"
              />
            </label>

            {error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[12.5px] text-rose-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !email || !password}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-slate-700 disabled:opacity-40 mt-2"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          {/* Single sign-on */}
          <div className="mt-6">
            <div className="flex items-center gap-3">
              <span className="h-px flex-1 bg-slate-200" />
              <span className="text-[11px] uppercase tracking-wide text-slate-400">
                or
              </span>
              <span className="h-px flex-1 bg-slate-200" />
            </div>
            {showSso ? (
              <form onSubmit={startSso} className="mt-4 space-y-2">
                <label className="block">
                  <span className="text-[12px] font-medium text-slate-700">
                    Workspace
                  </span>
                  <input
                    type="text"
                    value={ssoSlug}
                    onChange={(e) => setSsoSlug(e.target.value)}
                    autoFocus
                    placeholder="your-workspace"
                    className="mt-1.5 block w-full rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[14px] text-slate-900 placeholder-slate-400 focus:border-slate-400 focus:bg-white focus:outline-none transition-colors"
                  />
                  <span className="mt-1 block text-[11px] text-slate-400">
                    Your workspace identifier (ask your admin if unsure).
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={!ssoSlug.trim()}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-[14px] font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-40"
                >
                  Continue to your identity provider
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setShowSso(true)}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-[14px] font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                Sign in with SSO
              </button>
            )}
            <Link to="/link-sso" className="mt-3 block text-center text-sm text-slate-600 underline">
              Link SSO to an existing account
            </Link>
          </div>

          <p className="mt-6 text-center text-[11.5px] text-slate-400">
            Don&apos;t have an account yet?{" "}
            <Link
              to="/signup"
              className="text-slate-600 underline hover:text-slate-900 transition-colors"
            >
              Start a trial
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
